// @ts-check

/**
 * @typedef {Object} Dataset
 * @property {string} uuid
 * @property {string} id
 * @property {string} name
 * @property {string} definition
 * @property {number} order
 * @property {boolean} permission
 */

/**
 * @typedef {Object} Distribution
 * @property {string} uuid
 * @property {string} id
 * @property {string} name
 * @property {string} definition
 * @property {number} order
 * @property {boolean} permission
 */

/**
 * @typedef {Object} Group
 * @property {string} uuid
 * @property {string} name
 * @property {string} description
 * @property {Dataset[]} datasets
 * @property {Distribution[]} distributions
 * @property {Group[]} groups
 * @property {boolean} isExpanded
 * @property {number} order
 * @property {string | undefined} dsName
 * @property {string | undefined} dsDefinition
 * @property {string | undefined} dsId
 */

import constants from "src/components/editor/constants.js"
import apiRequest from 'src/mixins/api-request.js'
import { reverse } from 'src/lib/data/urls'

export default {
    mixins: [apiRequest],
    methods: {
        /** @returns {Promise<Group>} */
        fetchDataset: async function(id) {
            try {
                const res = await this.get(reverse("editor_dataset", [id]))
                return this.normalizeTree(res.data)
            } catch (err) {
                /** @type {Group} */
                const empty = {
                    uuid: "-",
                    name: "-",
                    description: "-",
                    datasets: [],
                    distributions: [],
                    groups: [],
                    isExpanded: false,
                    order: 0,
                    dsName: undefined,
                    dsDefinition: undefined,
                    dsId: undefined,
                }

                if (err.response && (err.response.status === constants.ErrorCodes.unauthorised || err.response.status === constants.ErrorCodes.forbidden)) {
                    empty.name = "You don't have permission to see this data set"
                } else {
                    empty.name = `Encountered error while fetching data set: ${err.message}`
                }

                return empty
            }
        },
        /** @returns {Dataset} */
        normalizeDataset: function(dataset) {
            const permission = dataset.included_dataset_name !== undefined
            return {
                uuid: dataset.id,
                id: dataset.included_dataset.toString(),
                name: permission ? dataset.included_dataset_name : "You do not have permission to view this data set.",
                definition: permission ? dataset.included_dataset_definition : "-",
                order: dataset.order,
                permission: permission
            }
        },
        /** @returns {Distribution} */
        normalizeDistribution: function(dist) {
            const permission = dist.distribution_name !== undefined
            return {
                uuid: dist.id,
                id: dist.distribution.toString(),
                name: permission ? dist.distribution_name : "You do not have permission to view this distribution.",
                definition: permission ? dist.distribution_definition : "-",
                order: dist.order,
                permission: permission
            }
        },
        /** @returns {Group} */
        normalizeGroup: function(group) {
            return {
                uuid: group.id,
                name: group.name,
                description: group.description,
                datasets: group['datasetdatasetpath_set'].map(this.normalizeDataset),
                distributions: group['datasetdistributionpath_set'].map(this.normalizeDistribution),
                groups: group['datasetdistributiongroup_set'].map(this.normalizeGroup),
                isExpanded: false,
                order: group.order,
                dsName: undefined,
                dsDefinition: undefined,
                dsId: undefined,
            }
        },
        /** @returns {Group} */
        normalizeTree: function(object) {
            // object should be the response from a request to /api/internal/editor/metadata/dataset/<id>
            const root = this.normalizeGroup(object['datasetdistributiongroup_set'])
            root.dsName = object.name
            root.dsDefinition = object.definition
            root.dsId = object.id
            return root
        }
    }
}
